import type { Location } from 'react-router-dom';

/**
 * Route guard type for auth/permission checking
 */
export type RouteGuard = React.ComponentType<{ children: React.ReactNode }>;

/**
 * Base route properties shared between parent and child routes
 */
interface BaseRouteProps {
  icon?: JSX.Element;
  badge?: string | number;
  prod?: boolean;
  guard?: RouteGuard;
}

/**
 * Child route configuration
 * Represents individual menu items within a route category
 */
export type RouteChildType = BaseRouteProps & {
  path: string;
  name: string;
  component: React.ComponentType<any>;
  tooltip?: string;
  disabled?: boolean;
  integrationId?: string;
  CustomSidebarComponent?: React.ComponentType<any>;
  makeSidebarPath?: () => string;
  /**
   * Generate CSS class names based on current location
   *
   * @param location - React Router location object with pathname
   * @returns CSS class name string (e.g., "has-shared-root-url")
   *
   * @example
   * ```ts
   * makeClassnames: (location) => {
   *   if (location.pathname.includes('/teams')) {
   *     return "has-shared-root-url";
   *   }
   *   return "";
   * }
   * ```
   */
  makeClassnames?: (location: Location) => string | undefined;
};

/**
 * Parent route configuration
 * Can either be a route with children (category) or a standalone route
 */
export type RouteType = BaseRouteProps & {
  id: string;
  path?: string; // Optional for categories, required for standalone routes
  children?: RouteChildType[] | null;
  component?: React.ComponentType<any> | null;
  containsHome?: boolean;
  open?: boolean;
  header?: string;
  classes?: string;
  makeSidebarPath?: () => string;
  makeClassnames?: (location: Location) => string | undefined;
};

/**
 * Type guard to check if a route is a category (has children)
 */
export const isRouteCategory = (
  route: RouteType,
): route is RouteType & { children: RouteChildType[] } => {
  return Boolean(route.children && route.children.length > 0);
};

/**
 * Type guard to check if a route is a standalone route (no children)
 */
export const isStandaloneRoute = (
  route: RouteType,
): route is RouteType & { path: string; component: React.ComponentType<any> } => {
  return Boolean(!route.children && route.path && route.component);
};
